/**
 * Trilipiderm Skin Quiz — section controller.
 *
 * Owns the DOM only. Every product decision comes from TrilipidermQuizEngine,
 * so the recommendation logic stays pure and testable.
 */
class SkinQuiz extends HTMLElement {
  constructor() {
    super();
    this.answers = this.blankAnswers();
    this.stepIndex = 0;
    this.result = null;
    this.tier = 'complete';
    this.bodyScents = { cleanser: 'ff', oil: 'ff' };
    // Hard rule 9: adding Lipid-E-Lips makes it the AM lip step.
    this.lipidELipsAdded = false;
    this.advanceTimer = null;
    this.warned = new Set();
  }

  blankAnswers() {
    return { q1: null, q2: null, q3: null, q4: [], q5: null, q6: null, q7: null, q8: null };
  }

  connectedCallback() {
    // connectedCallback fires again if the element is ever moved in the DOM;
    // without this guard every listener would be bound a second time.
    if (this.initialized) return;

    this.engine = window.TrilipidermQuizEngine;
    if (!this.engine) {
      console.error('[SkinQuiz] TrilipidermQuizEngine missing — check skin-quiz-engine.js loaded first');
      return;
    }

    this.catalog = this.readJSON('[data-quiz-catalog]') || {};
    this.copy = this.readJSON('[data-quiz-copy]') || {};

    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.questionsEl = this.querySelector('[data-quiz-questions]');
    this.resultsEl = this.querySelector('[data-quiz-results]');
    this.progressEl = this.querySelector('[data-quiz-progress]');
    this.backBtn = this.querySelector('[data-quiz-back]');
    this.continueBtn = this.querySelector('[data-quiz-continue]');
    this.progressTrack = this.querySelector('[data-quiz-progress-track]');
    this.progressBar = this.querySelector('[data-quiz-progress-bar]');
    this.progressLabel = this.querySelector('[data-quiz-progress-label]');
    this.emailStepEl = this.querySelector('[data-quiz-email-step]');
    this.emailForm = this.querySelector('[data-quiz-email-form]');
    this.emailNameInput = this.querySelector('[data-quiz-email-name]');
    this.emailInput = this.querySelector('[data-quiz-email-input]');
    this.emailSkipBtn = this.querySelector('[data-quiz-email-skip]');
    this.emailErrorEl = this.querySelector('[data-quiz-email-error]');

    if (!this.steps.length || !this.questionsEl || !this.resultsEl) {
      console.error('[SkinQuiz] markup incomplete — aborting');
      return;
    }

    this.auditCatalog();
    this.bindOptions();
    this.backBtn.addEventListener('click', () => this.goBack());
    this.continueBtn.addEventListener('click', () => this.goNext());
    this.resultsEl.addEventListener('click', (e) => this.onResultsClick(e));
    this.resultsEl.addEventListener('change', (e) => this.onResultsChange(e));

    if (this.emailForm) {
      this.emailForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.completeEmailStep(false);
      });
    }
    if (this.emailSkipBtn) {
      this.emailSkipBtn.addEventListener('click', () => this.completeEmailStep(true));
    }

    this.initialized = true;
    this.render();
  }

  readJSON(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[SkinQuiz] bad JSON in', selector, e);
      return null;
    }
  }

  /**
   * The engine's product slugs and the section's handle list are two lists that
   * have to stay in sync by hand. Say so loudly at load rather than silently
   * dropping a product out of somebody's routine.
   */
  auditCatalog() {
    const missing = Object.keys(this.engine.CATALOG_KEYS).filter((slug) => {
      const handle = this.engine.CATALOG_KEYS[slug].handle;
      return !this.catalog[handle];
    });
    if (missing.length) {
      console.warn(
        '[SkinQuiz] these products are referenced by the engine but absent from the section catalog ' +
          '(add their handles to quiz_handles in skin-quiz.liquid):',
        missing
      );
    }
  }

  /* ---------------------------------------------------------------- *
   * Question flow
   * ---------------------------------------------------------------- */

  bindOptions() {
    this.steps.forEach((step) => {
      const key = step.dataset.quizStep;
      const multi = step.dataset.quizType === 'multi';

      step.querySelectorAll('[data-quiz-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const value = btn.dataset.quizValue;

          if (multi) {
            const list = this.answers[key];
            const at = list.indexOf(value);
            if (at === -1) list.push(value);
            else list.splice(at, 1);
            btn.classList.toggle('is-selected', at === -1);
            btn.setAttribute('aria-pressed', at === -1 ? 'true' : 'false');
            this.updateControls();
            return;
          }

          step.querySelectorAll('[data-quiz-value]').forEach((b) => {
            b.classList.remove('is-selected');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          this.answers[key] = value;
          this.updateControls();

          // Single-select advances on its own; multi-select waits for Continue.
          // The step stays visible during the delay, so a second click would
          // otherwise queue a second advance and skip a whole question.
          if (this.advanceTimer) window.clearTimeout(this.advanceTimer);
          this.advanceTimer = window.setTimeout(() => {
            this.advanceTimer = null;
            this.goNext();
          }, 180);
        });
      });
    });
  }

  currentStep() {
    return this.steps[this.stepIndex];
  }

  goNext() {
    if (this.advanceTimer) {
      window.clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }
    if (this.stepIndex < this.steps.length - 1) {
      this.stepIndex += 1;
      this.render('forward', true);
      return;
    }
    this.finish();
  }

  goBack() {
    if (this.stepIndex === 0) return;
    if (this.advanceTimer) {
      window.clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }
    this.stepIndex -= 1;
    this.render('back', true);
  }

  render(direction, moveFocus) {
    // Drives the step-change animation; CSS reads it off the container.
    this.questionsEl.dataset.direction = direction || 'forward';

    this.steps.forEach((step, i) => {
      step.hidden = i !== this.stepIndex;
    });

    const total = this.steps.length;
    const current = this.stepIndex + 1;
    const pct = Math.round((current / total) * 100);
    const label = `Question ${current} of ${total}`;

    this.progressBar.style.width = pct + '%';
    this.progressLabel.textContent = label;
    if (this.progressTrack) {
      this.progressTrack.setAttribute('aria-valuenow', String(current));
      this.progressTrack.setAttribute('aria-valuemax', String(total));
      this.progressTrack.setAttribute('aria-valuetext', label);
    }

    this.backBtn.hidden = this.stepIndex === 0;
    this.updateControls();

    this.questionsEl.hidden = false;
    this.resultsEl.hidden = true;

    // The button that triggered the advance is now hidden, so focus would fall
    // back to the body and keyboard users would lose their place.
    if (moveFocus) {
      const step = this.currentStep();
      if (step) step.focus();
    }
  }

  updateControls() {
    const step = this.currentStep();
    if (!step) return;
    const multi = step.dataset.quizType === 'multi';
    // Only multi-select shows Continue — Q4 is optional, so it is always enabled.
    this.continueBtn.hidden = !multi;
  }

  finish() {
    this.result = this.engine.resolve(this.answers);
    this.tier = this.result.tier;
    this.bodyScents = { cleanser: 'ff', oil: 'ff' };
    this.lipidELipsAdded = false;
    this.questionsEl.hidden = true;
    if (this.progressEl) this.progressEl.hidden = true;

    if (this.emailStepEl) {
      this.showEmailStep();
      return;
    }
    this.showResults();
  }

  showEmailStep() {
    this.resultsEl.hidden = true;
    this.emailStepEl.hidden = false;
    this.emailStepEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.emailStepEl.focus({ preventScroll: true });
  }

  /** Runs on both submit and skip; only submit sends anything to Klaviyo. */
  completeEmailStep(skipped) {
    if (!skipped) {
      const email = (this.emailInput.value || '').trim();
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!valid) {
        this.emailInput.classList.add('is-invalid');
        if (this.emailErrorEl) this.emailErrorEl.hidden = false;
        this.emailInput.focus();
        return;
      }
      this.emailInput.classList.remove('is-invalid');
      if (this.emailErrorEl) this.emailErrorEl.hidden = true;
      this.submitToKlaviyo(email, (this.emailNameInput.value || '').trim());
    }

    this.emailStepEl.hidden = true;
    this.showResults();
  }

  showResults() {
    this.resultsEl.hidden = false;
    this.renderResults();
    this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.resultsEl.focus({ preventScroll: true });
  }

  /* ---------------------------------------------------------------- *
   * Klaviyo — fire-and-forget, never blocks the shopper from seeing
   * their results.
   * ---------------------------------------------------------------- */

  /** Reads the public company/site ID off the onsite embed script already on the page. */
  klaviyoCompanyId() {
    const script = document.querySelector('script[src*="static.klaviyo.com/onsite/js"][src*="company_id="]');
    if (!script) return null;
    const match = script.src.match(/company_id=([A-Za-z0-9]+)/);
    return match ? match[1] : null;
  }

  /** Visible label of the selected option for a single-select step. */
  answerLabel(stepKey) {
    const step = this.steps.find((s) => s.dataset.quizStep === stepKey);
    if (!step) return '';
    const selected = step.querySelector('.skin-quiz__option.is-selected .skin-quiz__option-label');
    return selected ? selected.textContent.trim() : '';
  }

  /** Visible labels of every selected option for a multi-select step. */
  answerLabels(stepKey) {
    const step = this.steps.find((s) => s.dataset.quizStep === stepKey);
    if (!step) return [];
    return Array.from(step.querySelectorAll('.skin-quiz__option.is-selected .skin-quiz__option-label')).map((el) =>
      el.textContent.trim()
    );
  }

  submitToKlaviyo(email, firstName) {
    const listId = this.dataset.klaviyoListId;
    const companyId = this.klaviyoCompanyId();
    if (!listId || !companyId) {
      console.warn('[SkinQuiz] Klaviyo list ID or company ID missing — skipping submission', { listId, companyId });
      return;
    }

    const r = this.result;
    const c = this.copy;
    const tierSlugs = this.tier === 'core' ? r.core : r.complete;
    const items = this.items(tierSlugs);
    const routineName = `${(c.resultTitlePrefix || '').trim()} ${r.routine.name}`.trim();

    const properties = {
      SkinQuiz: new Date().toISOString(),
      'Quiz Skin Feel': this.answerLabel('q1'),
      'Quiz Age Range': this.answerLabel('q2'),
      'Quiz Primary Goal': this.answerLabel('q3'),
      'Quiz Secondary Goals': this.answerLabels('q4'),
      'Quiz Fragrance Preference': this.answerLabel('q5'),
      'Quiz SPF Habit': this.answerLabel('q6'),
      'Quiz Routine Complexity': this.answerLabel('q7'),
      'Quiz Body Routine Interest': this.answerLabel('q8'),
      'Quiz Result': routineName,
      'Quiz Routine Tier': this.tier === 'core' ? c.coreLabel || 'Core routine' : c.completeLabel || 'Complete routine',
      'Quiz Recommended Products': items.map((i) => i.product.title),
      'Quiz Routine Price': this.formatMoney(items.reduce((sum, i) => sum + i.variant.price, 0)),
    };

    // The Subscribe endpoint only appears to apply profile properties the
    // first time a profile subscribes to this list — a retake with different
    // answers left the old ones in place. identify() always upserts profile
    // properties on every call, so it does that job; Subscribe is left to
    // only manage list consent.
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      const identifyPayload = Object.assign({ $email: email }, properties);
      if (firstName) identifyPayload.$first_name = firstName;
      window.klaviyo.identify(identifyPayload);
    }

    const profileAttributes = { email: email };
    if (firstName) profileAttributes.first_name = firstName;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: profileAttributes } },
          custom_source: 'Skin Quiz',
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      },
    };

    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-10-15',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) {
          return res.text().then((text) => {
            console.error('[SkinQuiz] Klaviyo submission rejected', res.status, text);
          });
        }
      })
      .catch((err) => console.error('[SkinQuiz] Klaviyo submission failed', err));
  }

  /* ---------------------------------------------------------------- *
   * Catalog lookup
   * ---------------------------------------------------------------- */

  /** Resolve an engine slug to { product, variant } from the Liquid catalog. */
  lookup(slug, overrides) {
    const base = this.engine.CATALOG_KEYS[slug];
    if (!base) return null;
    const key = Object.assign({}, base, overrides || {});
    if (!key.handle) return null;
    const product = this.catalog[key.handle];
    if (!product) return null;

    let variant = null;
    if (key.sku) variant = product.variants.find((v) => v.sku === key.sku);
    if (!variant && key.variant) variant = product.variants.find((v) => v.title === key.variant);

    // Falling through here means the SKU or variant title in the engine no
    // longer matches the store, so the shopper is about to be shown a different
    // size or scent than the spec calls for. Silence would hide a merchandising
    // change, so say something.
    if (!variant) {
      const wanted = key.sku ? `sku ${key.sku}` : `variant "${key.variant}"`;
      if (!this.warned.has(slug)) {
        this.warned.add(slug);
        console.warn(`[SkinQuiz] ${slug}: ${wanted} not found on ${key.handle}, falling back to first available variant`);
      }
      variant = product.variants.find((v) => v.available) || product.variants[0];
    }
    return variant ? { product, variant, slug } : null;
  }

  items(slugs) {
    return slugs.map((s) => this.lookup(s, this.variantOverride(s))).filter(Boolean);
  }

  /** Body scent choices override the default fragrance-free variant. */
  variantOverride(slug) {
    if (!this.result || !this.result.body || this.result.body.scentLocked) return null;
    const scents = this.engine.BODY_SCENTS;
    if (slug === 'body_cleanser_ff') {
      const pick = scents.cleanser.find((s) => s.key === this.bodyScents.cleanser);
      return pick ? { variant: pick.variant, sku: null } : null;
    }
    if (slug === 'body_oil_ff') {
      const pick = scents.oil.find((s) => s.key === this.bodyScents.oil);
      return pick ? { sku: pick.sku, variant: null } : null;
    }
    return null;
  }

  total(slugs) {
    return this.items(slugs).reduce((sum, i) => sum + i.variant.price, 0);
  }

  /**
   * Format cents using the store's own money_format, so non-USD stores keep
   * their currency symbol and totals over 999 keep their separator.
   */
  formatMoney(cents) {
    const format = this.copy.moneyFormat || '${{amount}}';
    const value = Number(cents) || 0;

    const group = (decimals, thousands, decimal) => {
      const fixed = (value / 100).toFixed(decimals);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
      switch (name) {
        case 'amount':
          return group(2, ',', '.');
        case 'amount_no_decimals':
          return group(0, ',', '.');
        case 'amount_with_comma_separator':
          return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return group(0, ' ', ',');
        case 'amount_with_space_separator':
          return group(2, ' ', ',');
        default:
          return group(2, ',', '.');
      }
    });
  }

  /* ---------------------------------------------------------------- *
   * Results
   * ---------------------------------------------------------------- */

  renderResults() {
    const r = this.result;
    const c = this.copy;
    const img = (c.routineImages || {})[r.primary];
    const tierSlugs = this.tier === 'core' ? r.core : r.complete;

    // All result copy is generated by the engine from the resolved routine.
    const problem = r.copy.skinProblem;
    const care = r.copy.careRoutine;

    this.resultsEl.innerHTML = `
      <p class="skin-quiz__eyebrow">${this.esc(c.resultEyebrow || 'Your result')}</p>
      <h2 class="skin-quiz__title">
        ${this.esc(c.resultTitlePrefix || 'Your Barrier-Repair')}
        <span class="skin-quiz__title-accent">${this.esc(r.routine.name)}</span>
      </h2>
      <p class="skin-quiz__headline">${this.esc(r.routine.headline)}</p>

      <div class="skin-quiz__hero${img ? '' : ' skin-quiz__hero--no-media'}">
        ${
          img
            ? `<div class="skin-quiz__hero-media"><img src="${this.esc(img)}" alt="" loading="lazy" width="1200" height="930"></div>`
            : ''
        }
        <div class="skin-quiz__hero-copy">
          <div class="skin-quiz__hero-block">
            <h3 class="skin-quiz__hero-label">${this.esc(c.skinProblemLabel || 'Skin problem')}</h3>
            <p>${this.esc(problem)}</p>
          </div>
          <div class="skin-quiz__hero-block">
            <h3 class="skin-quiz__hero-label">${this.esc(c.careRoutineLabel || 'Care routine')}</h3>
            <p>${this.esc(care)}</p>
          </div>
        </div>
      </div>

      ${this.renderTierToggle()}
      ${this.renderProducts(tierSlugs)}
      ${this.renderRoutineTable()}
      ${this.renderAddons()}
      ${this.renderBody()}

      <div class="skin-quiz__footer">
        <button type="button" class="skin-quiz__restart link" data-quiz-restart>
          ${this.esc(this.dataset.restartLabel || 'Retake the quiz')}
        </button>
      </div>
    `;
  }

  renderTierToggle() {
    const r = this.result;
    const c = this.copy;
    const coreTotal = this.formatMoney(this.total(r.core));
    const completeTotal = this.formatMoney(this.total(r.complete));
    const count = (n) => `${n} ${n === 1 ? 'product' : 'products'}`;
    return `
      <div class="skin-quiz__tiers" role="group" aria-label="Routine size">
        <button type="button" aria-pressed="${this.tier === 'core'}"
          class="skin-quiz__tier ${this.tier === 'core' ? 'is-active' : ''}" data-quiz-tier="core">
          <span class="skin-quiz__tier-name">${this.esc(c.coreLabel || 'Core routine')}</span>
          <span class="skin-quiz__tier-meta">${count(r.core.length)} · ${this.esc(coreTotal)}</span>
        </button>
        <button type="button" aria-pressed="${this.tier === 'complete'}"
          class="skin-quiz__tier ${this.tier === 'complete' ? 'is-active' : ''}" data-quiz-tier="complete">
          <span class="skin-quiz__tier-name">${this.esc(c.completeLabel || 'Complete routine')}</span>
          <span class="skin-quiz__tier-meta">${count(r.complete.length)} · ${this.esc(completeTotal)}</span>
        </button>
      </div>
    `;
  }

  renderProducts(slugs) {
    const items = this.items(slugs);
    const total = this.formatMoney(items.reduce((s, i) => s + i.variant.price, 0));
    return `
      <ul class="skin-quiz__products" role="list">
        ${items.map((i, n) => this.productCard(i, n + 1)).join('')}
      </ul>
      <div class="skin-quiz__cart">
        <p class="skin-quiz__cart-total">${items.length} products · <strong>${this.esc(total)}</strong></p>
        <button type="button" class="button skin-quiz__add" data-quiz-add="tier">Add routine to cart</button>
      </div>
    `;
  }

  productCard(item, step) {
    const v = item.variant;
    return `
      <li class="skin-quiz__product">
        <span class="skin-quiz__product-step">${step}</span>
        ${
          item.product.image
            ? `<img class="skin-quiz__product-image" src="${this.esc(item.product.image)}" alt="" loading="lazy" width="120" height="120">`
            : '<span class="skin-quiz__product-image skin-quiz__product-image--empty"></span>'
        }
        <span class="skin-quiz__product-info">
          <a class="skin-quiz__product-title" href="${this.esc(item.product.url)}">${this.esc(item.product.title)}</a>
          <span class="skin-quiz__product-variant">${this.esc(v.title === 'Default Title' ? '' : v.title)}</span>
        </span>
        <span class="skin-quiz__product-price">${this.esc(v.price_formatted)}</span>
      </li>
    `;
  }

  /** AM / PM split for result-page display (spec section 6). */
  renderRoutineTable() {
    const r = this.result;
    const rows = this.engine.schedule(r, { lipidELipsAdded: this.lipidELipsAdded });
    const name = (slug) => {
      const hit = this.lookup(slug);
      return hit ? hit.product.title : '';
    };
    const cell = (list) => {
      if (!list || !list.length) return '<span class="skin-quiz__dash">—</span>';
      return list.map((s) => this.esc(name(s))).join('<br>');
    };
    return `
      <div class="skin-quiz__schedule">
        <h3 class="skin-quiz__section-heading">Morning and evening</h3>
        <table class="skin-quiz__table">
          <thead><tr><th scope="col">Step</th><th scope="col">AM</th><th scope="col">PM</th></tr></thead>
          <tbody>
            ${rows
              .map(
                (row) => `
              <tr><th scope="row">${this.esc(row.label)}</th><td>${cell(row.am)}</td><td>${cell(row.pm)}</td></tr>`
              )
              .join('')}
          </tbody>
        </table>
        ${r.scheduleNote ? `<p class="skin-quiz__note">${this.esc(r.scheduleNote)}</p>` : ''}
      </div>
    `;
  }

  renderAddons() {
    const r = this.result;
    if (!r.addons.length) return '';
    const c = this.copy;
    const items = this.items(r.addons);
    if (!items.length) return '';
    return `
      <div class="skin-quiz__addons">
        <h3 class="skin-quiz__section-heading">${this.esc(c.addonsLabel || 'Optional add-ons')}</h3>
        <p class="skin-quiz__note">Not included in the routine price.</p>
        <ul class="skin-quiz__products" role="list">
          ${items
            .map(
              (i) => `
            <li class="skin-quiz__product">
              ${
                i.product.image
                  ? `<img class="skin-quiz__product-image" src="${this.esc(i.product.image)}" alt="" loading="lazy" width="120" height="120">`
                  : '<span class="skin-quiz__product-image skin-quiz__product-image--empty"></span>'
              }
              <span class="skin-quiz__product-info">
                <a class="skin-quiz__product-title" href="${this.esc(i.product.url)}">${this.esc(i.product.title)}</a>
                <span class="skin-quiz__product-variant">${this.esc(i.variant.title === 'Default Title' ? '' : i.variant.title)}</span>
                ${
                  (r.addonNotes || {})[i.slug]
                    ? `<span class="skin-quiz__product-note">${this.esc(r.addonNotes[i.slug])}</span>`
                    : ''
                }
              </span>
              <span class="skin-quiz__product-price">${this.esc(i.variant.price_formatted)}</span>
              <button type="button" class="button button--secondary skin-quiz__add-one"
                data-quiz-add-variant="${i.variant.id}" data-quiz-add-slug="${this.esc(i.slug)}">Add</button>
            </li>`
            )
            .join('')}
        </ul>
      </div>
    `;
  }

  renderBody() {
    const r = this.result;
    if (!r.body) return '';
    const c = this.copy;
    const items = this.items(r.body.items);
    if (!items.length) return '';
    const total = this.formatMoney(items.reduce((s, i) => s + i.variant.price, 0));

    const scentPicker = r.body.scentLocked
      ? '<p class="skin-quiz__note">Fragrance-free, to match your skin result.</p>'
      : `
        <div class="skin-quiz__scents">
          <label class="skin-quiz__scent">
            <span>Body cleanser scent</span>
            <select data-quiz-scent="cleanser">
              ${this.engine.BODY_SCENTS.cleanser
                .map(
                  (s) =>
                    `<option value="${this.esc(s.key)}" ${s.key === this.bodyScents.cleanser ? 'selected' : ''}>${this.esc(s.label)}</option>`
                )
                .join('')}
            </select>
          </label>
          <label class="skin-quiz__scent">
            <span>Body oil scent</span>
            <select data-quiz-scent="oil">
              ${this.engine.BODY_SCENTS.oil
                .map(
                  (s) =>
                    `<option value="${this.esc(s.key)}" ${s.key === this.bodyScents.oil ? 'selected' : ''}>${this.esc(s.label)}</option>`
                )
                .join('')}
            </select>
          </label>
        </div>`;

    return `
      <div class="skin-quiz__body-module">
        <h3 class="skin-quiz__section-heading">${this.esc(c.bodyLabel || 'Add your body routine')}</h3>
        ${r.body.leadNote ? `<p class="skin-quiz__note">${this.esc(r.body.leadNote)}</p>` : ''}
        ${scentPicker}
        <ul class="skin-quiz__products" role="list">
          ${items.map((i, n) => this.productCard(i, n + 1)).join('')}
        </ul>
        <div class="skin-quiz__cart">
          <p class="skin-quiz__cart-total">${items.length} products · <strong>${this.esc(total)}</strong></p>
          <button type="button" class="button button--secondary skin-quiz__add" data-quiz-add="body">Add body routine</button>
        </div>
      </div>
    `;
  }

  /* ---------------------------------------------------------------- *
   * Results interactions
   * ---------------------------------------------------------------- */

  onResultsClick(e) {
    const tier = e.target.closest('[data-quiz-tier]');
    if (tier) {
      this.tier = tier.dataset.quizTier;
      this.renderResults();
      const restored = this.resultsEl.querySelector(`[data-quiz-tier="${this.tier}"]`);
      if (restored) restored.focus();
      return;
    }

    const restart = e.target.closest('[data-quiz-restart]');
    if (restart) {
      this.answers = this.blankAnswers();
      this.stepIndex = 0;
      this.result = null;
      this.lipidELipsAdded = false;
      this.bodyScents = { cleanser: 'ff', oil: 'ff' };
      this.querySelectorAll('.skin-quiz__option').forEach((b) => {
        b.classList.remove('is-selected');
        if (b.hasAttribute('aria-pressed')) b.setAttribute('aria-pressed', 'false');
      });
      if (this.emailForm) this.emailForm.reset();
      if (this.emailInput) this.emailInput.classList.remove('is-invalid');
      if (this.emailErrorEl) this.emailErrorEl.hidden = true;
      if (this.emailStepEl) this.emailStepEl.hidden = true;
      if (this.progressEl) this.progressEl.hidden = false;
      this.render('back', true);
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const addOne = e.target.closest('[data-quiz-add-variant]');
    if (addOne) {
      const slug = addOne.dataset.quizAddSlug;
      this.addToCart([{ id: Number(addOne.dataset.quizAddVariant), quantity: 1 }], addOne).then((ok) => {
        // Hard rule 9: Lipid-E-Lips becomes the AM lip product once added, and
        // the Rosehip balm drops to PM only.
        if (ok && slug === 'lipid_e_lips' && !this.lipidELipsAdded) {
          this.lipidELipsAdded = true;
          this.renderResults();
        }
      });
      return;
    }

    const add = e.target.closest('[data-quiz-add]');
    if (add) {
      const which = add.dataset.quizAdd;
      const slugs = which === 'body' ? this.result.body.items : this.tier === 'core' ? this.result.core : this.result.complete;
      // Each slug appears once, so the Day Creme is charged once even though
      // the schedule shows it at both the AM moisturize and protect steps.
      const lines = this.items(slugs).map((i) => ({ id: i.variant.id, quantity: 1 }));
      this.addToCart(lines, add);
    }
  }

  onResultsChange(e) {
    const scent = e.target.closest('[data-quiz-scent]');
    if (!scent) return;
    const which = scent.dataset.quizScent;
    this.bodyScents[which] = scent.value;
    this.renderResults();
    // renderResults replaces the select, so hand focus back to its replacement.
    const restored = this.resultsEl.querySelector(`[data-quiz-scent="${which}"]`);
    if (restored) restored.focus();
  }

  /**
   * Add line items to the cart. Resolves true on success, false on failure —
   * a rejected add (sold out, quantity limit) still returns parseable JSON, so
   * the response status is the only reliable signal.
   */
  addToCart(lines, button) {
    if (!lines.length) return Promise.resolve(false);

    const original = button.textContent;
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    button.setAttribute('disabled', 'disabled');
    button.textContent = 'Adding…';
    this.clearCartError();

    return fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          this.showCartError((data && (data.description || data.message)) || 'We could not add that to your cart.');
          return false;
        }
        return this.refreshCart().then(() => true);
      })
      .catch((err) => {
        console.error('[SkinQuiz] add to cart failed', err);
        this.showCartError('Something went wrong adding to your cart. Please try again.');
        return false;
      })
      .finally(() => {
        button.removeAttribute('disabled');
        button.textContent = original;
      });
  }

  showCartError(message) {
    this.clearCartError();
    const el = document.createElement('p');
    el.className = 'skin-quiz__error';
    el.setAttribute('role', 'alert');
    el.dataset.quizError = '';
    el.textContent = message;
    this.resultsEl.appendChild(el);
  }

  clearCartError() {
    const existing = this.resultsEl.querySelector('[data-quiz-error]');
    if (existing) existing.remove();
  }

  /** Refresh the theme's cart drawer / icon after adding. */
  refreshCart() {
    const drawer = document.querySelector('cart-drawer');
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    return fetch(`${root}?section_id=cart-drawer`)
      .then((res) => res.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        const bubbleTarget = document.getElementById('cart-icon-bubble');
        const bubbleSource = parsed.getElementById('cart-icon-bubble');
        if (bubbleTarget && bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;

        if (!drawer) return;
        const inner = drawer.querySelector('.drawer__inner');
        const sourceInner = parsed.querySelector('.drawer__inner');
        if (inner && sourceInner) inner.innerHTML = sourceInner.innerHTML;

        const sourceDrawer = parsed.querySelector('cart-drawer');
        if (sourceDrawer) drawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));

        if (typeof drawer.open === 'function') drawer.open();
      })
      .catch((err) => console.error('[SkinQuiz] cart refresh failed', err));
  }

  esc(value) {
    if (value == null) return '';
    return String(value).replace(/[&<>"']/g, (ch) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }
}

customElements.define('skin-quiz', SkinQuiz);
